// CarouselStudio — AI-generated multi-slide branded carousels.
// Generates a cover + content slides + CTA slide, each rendered client-side via
// the shared CreativeCanvas. Exports all slides as PNGs or saves them as a
// single carousel draft post (media items attached in order). $0 compute.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toBlob } from 'html-to-image';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { CreativeCanvas, SIZES, TEMPLATES, DEFAULT_OVERRIDES } from './CreativeCanvas';
import {
  Sparkles, Loader2, Wand2, Save, Download, Upload, ChevronLeft, ChevronRight,
  Plus, Trash2, Images, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'Bluesky', 'X'];
const TONES = ['Professional', 'Friendly', 'Bold', 'Playful', 'Inspirational', 'Luxury'];
const CAROUSEL_SIZES = { '1:1': SIZES['1:1'], '4:5': SIZES['4:5'] };
const ROLE_LABEL = { cover: 'Cover', content: 'Slide', cta: 'CTA' };

const blankSlide = (role = 'content') => ({
  role,
  headline: 'Headline',
  subheadline: 'Add a short supporting line',
  cta: '',
  template: 'spotlight',
  bgImage: null,
  overrides: { ...DEFAULT_OVERRIDES },
});

export const CarouselStudio = ({ kits = [] }) => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('Instagram');
  const [tone, setTone] = useState('Professional');
  const [size, setSize] = useState('4:5');
  const [kitId, setKitId] = useState(kits[0]?.id || '');
  const [slideCount, setSlideCount] = useState(5);
  const [slides, setSlides] = useState([]);
  const [active, setActive] = useState(0);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');

  const [generating, setGenerating] = useState(false);
  const [generatingBg, setGeneratingBg] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const renderRefs = useRef({});
  const kit = kits.find((k) => k.id === kitId) || null;
  const { w, h } = CAROUSEL_SIZES[size] || SIZES['1:1'];
  const previewScale = 360 / w;

  const cur = slides[active] || null;
  const patchSlide = (idx, patch) => setSlides((s) => s.map((sl, i) => (i === idx ? { ...sl, ...patch } : sl)));

  const generate = async () => {
    if (topic.trim().length < 3) { toast.error('Enter a topic first'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/creatives/generate-carousel', {
        topic, platform, tone, slide_count: slideCount, brand_name: kit?.name || null,
      });
      const built = (r.data.slides || []).map((s) => ({
        ...blankSlide(s.role),
        headline: s.headline || 'Headline',
        subheadline: s.subheadline || '',
        cta: s.cta || '',
        template: s.role === 'cover' ? 'spotlight' : (s.role === 'cta' ? 'card' : 'split'),
      }));
      if (!built.length) { toast.error('No slides returned — try again'); return; }
      setSlides(built);
      setActive(0);
      setCaption(r.data.caption || '');
      setHashtags(r.data.hashtags || '');
      toast.success(`Generated ${built.length}-slide carousel`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const uploadBg = async (file) => {
    if (!file || active < 0) return;
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      patchSlide(active, { bgImage: r.data.url });
      toast.success('Background added to slide');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingBg(false); }
  };

  const generateBg = async () => {
    if (!cur) return;
    const prompt = (topic.trim() || cur.headline || '').trim();
    if (prompt.length < 3) { toast.error('Enter a topic to generate a background'); return; }
    setGeneratingBg(true);
    try {
      const r = await api.post('/generate/image', { prompt, model: 'nano-banana' });
      if (r.data?.image_base64) {
        patchSlide(active, { bgImage: `data:image/png;base64,${r.data.image_base64}` });
        toast.success('AI background added to slide');
      } else { toast.error('No image returned'); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Background generation failed'));
    } finally { setGeneratingBg(false); }
  };

  const addSlide = () => {
    setSlides((s) => {
      const next = [...s];
      const insertAt = Math.min(active + 1, next.length);
      next.splice(insertAt, 0, blankSlide('content'));
      setActive(insertAt);
      return next;
    });
  };

  const removeSlide = (idx) => {
    if (slides.length <= 1) { toast.error('A carousel needs at least one slide'); return; }
    setSlides((s) => s.filter((_, i) => i !== idx));
    setActive((a) => Math.max(0, a >= idx ? a - 1 : a));
  };

  // Render one slide's off-screen full-size node to a PNG blob.
  const renderSlideBlob = async (idx) => {
    const node = renderRefs.current[idx];
    if (!node) throw new Error('render node missing');
    await toBlob(node, { width: w, height: h, cacheBust: true, pixelRatio: 1, skipFonts: true });
    return toBlob(node, { width: w, height: h, cacheBust: true, pixelRatio: 1, skipFonts: true });
  };

  const downloadAll = async () => {
    if (!slides.length) return;
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      for (let i = 0; i < slides.length; i += 1) {
        const blob = await renderSlideBlob(i); // eslint-disable-line no-await-in-loop
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `carousel-${i + 1}.png`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        await new Promise((r) => setTimeout(r, 150)); // eslint-disable-line no-await-in-loop
      }
      toast.success(`Downloaded ${slides.length} slides`);
    } catch (e) { toast.error('Could not render slides'); }
    finally { setExporting(false); }
  };

  const saveAsDraft = async () => {
    if (!slides.length) { toast.error('Generate a carousel first'); return; }
    if (!caption.trim()) { toast.error('Add a caption first'); return; }
    setSaving(true);
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      const media_items = [];
      for (let i = 0; i < slides.length; i += 1) {
        const blob = await renderSlideBlob(i); // eslint-disable-line no-await-in-loop
        const file = new File([blob], `carousel-${Date.now()}-${i + 1}.png`, { type: 'image/png' });
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); // eslint-disable-line no-await-in-loop
        media_items.push(up.data);
      }
      await api.post('/posts', {
        caption,
        hashtags,
        platforms: [platform],
        post_title: slides[0]?.headline || 'Carousel',
        post_type: 'carousel',
        media_items,
      });
      toast.success('Carousel saved as draft in My Posts');
      navigate('/posts');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save carousel'); }
    finally { setSaving(false); setExporting(false); }
  };

  return (
    <div data-testid="carousel-studio">
      {/* Generator controls */}
      <Card><CardContent className="p-4 space-y-4">
        <div className="space-y-1">
          <Label className="text-xs">What's the carousel about?</Label>
          <Textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. 5 tips to grow your email list as a small business"
            data-testid="carousel-topic" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}><SelectTrigger data-testid="carousel-platform"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tone</Label>
            <Select value={tone} onValueChange={setTone}><SelectTrigger data-testid="carousel-tone"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Size</Label>
            <Select value={size} onValueChange={setSize}><SelectTrigger data-testid="carousel-size"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CAROUSEL_SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Slides</Label>
            <Select value={String(slideCount)} onValueChange={(v) => setSlideCount(parseInt(v, 10))}>
              <SelectTrigger data-testid="carousel-count"><SelectValue /></SelectTrigger>
              <SelectContent>{[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <SelectItem key={n} value={String(n)}>{n} slides</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Brand kit</Label>
            <Select value={kitId || 'none'} onValueChange={(v) => setKitId(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="carousel-brandkit"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Default colors</SelectItem>
                {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
              </SelectContent></Select>
          </div>
        </div>
        <Button onClick={generate} disabled={generating} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-carousel-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />} Generate carousel with AI
        </Button>
      </CardContent></Card>

      {slides.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Left: slide strip + editor */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5"><Images className="w-3.5 h-3.5 text-indigo-600" /> Slides ({slides.length})</Label>
                <Button variant="outline" size="sm" onClick={addSlide} data-testid="carousel-add-slide"><Plus className="w-3.5 h-3.5 mr-1" /> Add slide</Button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="carousel-slide-strip">
                {slides.map((s, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`carousel-slide-thumb-${i}`}
                    className={`relative shrink-0 w-14 h-16 rounded-md border-2 text-[10px] font-semibold flex items-center justify-center transition-colors ${active === i ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-zinc-200 bg-zinc-50 text-zinc-400 hover:border-zinc-300'}`}>
                    {i + 1}
                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] px-1 rounded-full bg-zinc-700 text-white leading-tight">{ROLE_LABEL[s.role] || 'Slide'}</span>
                  </button>
                ))}
              </div>
            </CardContent></Card>

            {cur && (
              <Card><CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Editing slide {active + 1} · {ROLE_LABEL[cur.role]}</Label>
                  <button onClick={() => removeSlide(active)} className="text-[11px] text-red-500 hover:text-red-600 flex items-center gap-1" data-testid="carousel-remove-slide">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
                <div><Label className="text-xs">Headline</Label><Input value={cur.headline} onChange={(e) => patchSlide(active, { headline: e.target.value })} data-testid="carousel-edit-headline" /></div>
                <div><Label className="text-xs">Subheadline</Label><Textarea rows={2} value={cur.subheadline} onChange={(e) => patchSlide(active, { subheadline: e.target.value })} data-testid="carousel-edit-subheadline" /></div>
                <div><Label className="text-xs">CTA (optional)</Label><Input value={cur.cta} onChange={(e) => patchSlide(active, { cta: e.target.value })} data-testid="carousel-edit-cta" /></div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" /> Template</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {TEMPLATES.map((t) => (
                      <button key={t.id} onClick={() => patchSlide(active, { template: t.id })} data-testid={`carousel-template-${t.id}`}
                        className={`text-left rounded-md border p-2 transition-colors ${cur.template === t.id ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200 hover:border-zinc-300'}`}>
                        <p className="text-xs font-semibold">{t.name}</p>
                        <p className="text-[10px] text-zinc-500 leading-tight">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Slide background (optional)</Label>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} data-testid="carousel-bg-input" />
                      <span className="inline-flex items-center text-sm border border-zinc-200 rounded-md px-3 h-9 hover:bg-zinc-50">
                        {uploadingBg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} {cur.bgImage ? 'Replace' : 'Upload'}
                      </span>
                    </label>
                    {cur.bgImage && <Button variant="ghost" size="sm" onClick={() => patchSlide(active, { bgImage: null })}>Remove</Button>}
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-1" onClick={generateBg} disabled={generatingBg} data-testid="carousel-generate-bg">
                    {generatingBg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5 text-indigo-600" />}
                    Generate AI background (Nano Banana)
                  </Button>
                </div>
              </CardContent></Card>
            )}

            <Card><CardContent className="p-4 space-y-3">
              <div><Label className="text-xs">Post caption</Label><Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="carousel-caption" /></div>
              <div><Label className="text-xs">Hashtags</Label><Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} data-testid="carousel-hashtags" /></div>
            </CardContent></Card>
          </div>

          {/* Right: preview + actions */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 flex flex-col items-center gap-4">
              <div className="flex items-center gap-3 w-full justify-center">
                <Button variant="ghost" size="icon" onClick={() => setActive((a) => Math.max(0, a - 1))} disabled={active === 0} data-testid="carousel-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-xs text-zinc-500" data-testid="carousel-position">Slide {active + 1} of {slides.length}</span>
                <Button variant="ghost" size="icon" onClick={() => setActive((a) => Math.min(slides.length - 1, a + 1))} disabled={active === slides.length - 1} data-testid="carousel-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
              {cur && (
                <div className="rounded-lg overflow-hidden shadow-lg border border-zinc-200" style={{ width: w * previewScale, height: h * previewScale }} data-testid="carousel-preview">
                  <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: w, height: h }}>
                    <CreativeCanvas template={cur.template} size={size} kit={kit}
                      content={{ headline: cur.headline, subheadline: cur.subheadline, cta: cur.cta }}
                      bgImage={cur.bgImage} overrides={cur.overrides} />
                  </div>
                </div>
              )}
              <div className="flex gap-2 w-full">
                <Button onClick={saveAsDraft} disabled={saving || exporting} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-carousel-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save as draft
                </Button>
                <Button variant="outline" onClick={downloadAll} disabled={exporting} data-testid="download-carousel-btn">
                  {exporting && !saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} All PNGs
                </Button>
              </div>
            </CardContent></Card>
          </div>
        </div>
      )}

      {/* Off-screen full-size render nodes for export */}
      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden="true">
        {slides.map((s, i) => (
          <div key={i} style={{ width: w, height: h }}>
            <CreativeCanvas ref={(el) => { renderRefs.current[i] = el; }}
              template={s.template} size={size} kit={kit}
              content={{ headline: s.headline, subheadline: s.subheadline, cta: s.cta }}
              bgImage={s.bgImage} overrides={s.overrides} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CarouselStudio;
